import { ChartLineIcon } from "@phosphor-icons/react";
import {
  CategoryScale,
  Chart,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  TooltipModel,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import { Card } from "flowbite-react";
import { useCallback, useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../lib/utils";
import { APP_PLATFORM_LOGO_MAP } from "../../../shared/constants";
import { IRevenueVsSpendEntity } from "../interface";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

interface RevenueAndSpendChartProps {
  data?: IRevenueVsSpendEntity;
  showWithTax: boolean;
  connectedPlatforms?: Set<string>;
}

// ── Tooltip helpers ───────────────────────────────────────────────────────────

function fmt(v: number) {
  return formatCurrency(v ?? 0, "USD");
}

function row(label: string, value: number, color?: string, bold = false) {
  return `<div style="display:flex;justify-content:space-between;align-items:center;gap:16px;min-width:180px">
    <span style="font-size:11px;color:${color ?? "#d1d5db"};${bold ? "font-weight:700" : ""}">${label}</span>
    <span style="font-size:11px;font-weight:700;color:${bold ? "#fff" : "#e5e7eb"};white-space:nowrap">${fmt(value)}</span>
  </div>`;
}

function subRow(iconSrc: string, label: string, value: number) {
  if (!value && value !== 0) return "";
  return `<div style="display:flex;justify-content:space-between;align-items:center;gap:16px;padding-left:12px;border-left:2px solid #374151">
    <span style="display:flex;align-items:center;gap:5px;font-size:10px;color:#9ca3af">
      <img src="${iconSrc}" style="width:11px;height:11px;object-fit:contain" />
      ${label}
    </span>
    <span style="font-size:10px;font-weight:600;color:#d1d5db;white-space:nowrap">${fmt(value)}</span>
  </div>`;
}

function divider() {
  return `<div style="border-top:1px solid #374151;margin:6px 0"></div>`;
}

// ── Component ─────────────────────────────────────────────────────────────────

const RevenueAndSpendChart = ({
  data,
  showWithTax,
  connectedPlatforms,
}: RevenueAndSpendChartProps) => {
  const { t } = useTranslation();
  const chartRef = useRef<Chart<"line"> | null>(null);

  // Build external tooltip HTML
  const buildTooltip = useCallback(
    (index: number) => {
      if (!data) return "";
      const wt = showWithTax;
      const isConnected = (key: string) =>
        !connectedPlatforms || connectedPlatforms.size === 0 || connectedPlatforms.has(key) ||
        (key === "apple_ads" && connectedPlatforms.has("apple_search_ads"));

      const admob = data.estimateRevenue?.[index] ?? 0;
      const iap = data.iapRevenue?.[index] ?? 0;
      const totalRev = data.totalRevenue?.[index] ?? (admob + iap);

      const cost = wt ? data.totalCost_with_tax?.[index] : data.totalCost_without_tax?.[index];
      const gCost = wt ? data.googleAdsCost_with_tax?.[index] : data.googleAdsCost_without_tax?.[index];
      const aCost = wt ? data.appleAdsCost_with_tax?.[index] : data.appleAdsCost_without_tax?.[index];
      const mCost = wt ? data.metaAdsCost_with_tax?.[index] : data.metaAdsCost_without_tax?.[index];
      const net = wt ? data.totalNet_with_tax?.[index] : data.totalNet_without_tax?.[index];

      const hasIap = isConnected("apple_iap") || isConnected("google_iap");
      return `
        ${row("Revenue", totalRev, "#22c55e", true)}
        <div style="display:flex;flex-direction:column;gap:3px;margin-top:3px">
          ${isConnected("google_admob") ? subRow(APP_PLATFORM_LOGO_MAP.google_admob, "AdMob", admob) : ""}
          ${hasIap ? subRow(APP_PLATFORM_LOGO_MAP.apple, "IAP", iap) : ""}
        </div>
        ${divider()}
        ${row("Total Cost", cost ?? 0, "#ef4444", true)}
        <div style="display:flex;flex-direction:column;gap:3px;margin-top:3px">
          ${isConnected("google_ads") ? subRow(APP_PLATFORM_LOGO_MAP.google_ads, "Google Ads", gCost ?? 0) : ""}
          ${isConnected("apple_ads") ? subRow(APP_PLATFORM_LOGO_MAP.app_store, "Apple Ads", aCost ?? 0) : ""}
          ${isConnected("meta_ads") ? subRow(APP_PLATFORM_LOGO_MAP.meta_ads, "Meta Ads", mCost ?? 0) : ""}
        </div>
        ${divider()}
        ${row("Net", net ?? 0, "#818cf8", true)}
      `;
    },
    [data, showWithTax, connectedPlatforms]
  );

  // External tooltip handler
  const externalTooltip = useCallback(
    (context: { chart: Chart; tooltip: TooltipModel<"line"> }) => {
      const { chart, tooltip } = context;
      const tooltipEl = document.getElementById("revenue-chart-tooltip");
      if (!tooltipEl) return;

      if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = "0";
        tooltipEl.style.transform = "translateY(6px) scale(0.97)";
        return;
      }

      const index = tooltip.dataPoints?.[0]?.dataIndex ?? 0;
      const label = tooltip.dataPoints?.[0]?.label ?? "";

      const dateStr = (() => {
        if (!label) return "";
        const datePart = label.includes("T") ? label.split("T")[0] : label;
        const parts = datePart.split("-");
        if (parts.length === 3) {
          const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
          return `${parts[2]} ${months[parseInt(parts[1]) - 1]} ${parts[0]}`;
        }
        return label;
      })();

      tooltipEl.innerHTML = `
        <div style="font-size:12px;font-weight:700;color:#fff;margin-bottom:8px">${dateStr}</div>
        <div style="display:flex;flex-direction:column;gap:4px">
          ${buildTooltip(index)}
        </div>
      `;

      // getBoundingClientRect gives viewport-relative coords — required for position:fixed
      const canvasRect = chart.canvas.getBoundingClientRect();
      const x = canvasRect.left + tooltip.caretX;
      const y = canvasRect.top + tooltip.caretY;

      // Keep tooltip inside viewport (flip left if near right edge, clamp vertically)
      const tooltipWidth = tooltipEl.offsetWidth || 220;
      const tooltipHeight = tooltipEl.offsetHeight || 200;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const left = x + tooltipWidth + 16 > viewportWidth ? x - tooltipWidth - 16 : x + 16;
      const top = Math.max(8, Math.min(y - tooltipHeight / 2, viewportHeight - tooltipHeight - 8));

      tooltipEl.style.left = `${left}px`;
      tooltipEl.style.top = `${top}px`;

      // Trigger entrance animation after position is set
      requestAnimationFrame(() => {
        tooltipEl.style.opacity = "1";
        tooltipEl.style.transform = "translateY(0) scale(1)";
      });
    },
    [buildTooltip]
  );

  // Active chart data (respects showWithTax toggle)
  const activeChartData: ChartData<"line"> = {
    labels: data?.labels || [],
    datasets: [
      {
        label: t("dashboard.revenueVsSpend.revenue"),
        data: data?.totalRevenue || data?.estimateRevenue || [],
        borderColor: "#22c55e",
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 2,
        pointHoverRadius: 5,
      },
      {
        label: t("dashboard.revenueVsSpend.spend"),
        data: showWithTax ? data?.totalCost_with_tax || [] : data?.totalCost_without_tax || [],
        borderColor: "#ef4444",
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 2,
        pointHoverRadius: 5,
      },
      {
        label: t("dashboard.revenueVsSpend.net"),
        data: showWithTax ? data?.totalNet_with_tax || [] : data?.totalNet_without_tax || [],
        borderColor: "#818cf8",
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 2,
        pointHoverRadius: 5,
      },
    ],
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: { size: 12, weight: "bold" },
          color: "var(--text-color)",
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 12,
          padding: 16,
        },
      },
      tooltip: {
        enabled: false,
        external: externalTooltip,
      },
      zoom: {
        pan: { enabled: true, mode: "x" },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: "x",
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 20,
          font: { family: "var(--font-inter)", size: 11 },
          color: "#6b7280",
          callback: function (_value, index) {
            const label = this.getLabelForValue(index);
            if (label && typeof label === "string") {
              const datePart = label.includes("T") ? label.split("T")[0] : label;
              const parts = datePart.split("-");
              if (parts.length === 3) {
                const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                return `${parts[2]} ${months[parseInt(parts[1]) - 1]}`;
              }
            }
            return label;
          },
        },
      },
      y: {
        grid: { display: false },
        ticks: {
          font: { family: "var(--font-inter)", size: 12 },
          color: "#6b7280",
          callback: function (tickValue: number | string) {
            if (typeof tickValue === "number") return formatCurrency(tickValue, "USD");
            return tickValue;
          },
        },
      },
    },
  };

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    const height = chartArea.bottom;
    const gradients: [string, string][] = [
      ["rgba(34, 197, 94, 0.35)", "rgba(34, 197, 94, 0)"],
      ["rgba(239, 68, 68, 0.2)", "rgba(239, 68, 68, 0)"],
      ["rgba(129, 140, 248, 0.35)", "rgba(129, 140, 248, 0)"],
    ];
    gradients.forEach(([start, end], i) => {
      const g = ctx.createLinearGradient(0, 0, 0, height);
      g.addColorStop(0, start);
      g.addColorStop(1, end);
      chart.data.datasets[i].backgroundColor = g;
    });
    chart.update();
  }, [data, showWithTax]);

  return (
    <div >
      {/* External tooltip element — lives outside chart DOM */}
      <div
        id="revenue-chart-tooltip"
        style={{
          position: "fixed",
          background: "#1f2937",
          border: "1px solid #374151",
          borderRadius: "10px",
          padding: "12px 14px",
          pointerEvents: "none",
          transition: "opacity 0.18s ease, transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1)",
          opacity: 0,
          transform: "translateY(6px) scale(0.97)",
          zIndex: 9999,
          minWidth: "200px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          willChange: "transform, opacity",
        }}
      />

      <div className="flex justify-between items-center mb-2 pl-2">
        <div className="flex items-center gap-2">
          <ChartLineIcon size={20} className="text-gray-700" weight="duotone" />
          <h1 className="text-md font-bold text-gray-700">
            {t("dashboard.revenueVsSpend.title")}
          </h1>
        </div>
      </div>
      <Card
        theme={{
          root: {
            base: "flex rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800",
          },
        }}
      >
        <div className="h-80">
          <Line ref={chartRef} data={activeChartData} options={options} />
        </div>
      </Card>
    </div>
  );
};

export default RevenueAndSpendChart;
